import type { LocalViewId, ViewKind } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createContainer,
	createStore,
	createActionsHook,
	type Action,
	type BoundActions,
	type HookActionsFunction,
	type OverrideContainerComponent,
	type Store,
} from '@atlassian/react-sweet-state';
import type { Props, State } from './NavigationView.types.tsx';
import { loadViews } from './loadViews.tsx';
import { refreshView } from './refreshView.tsx';
import { refreshViews } from './refreshViews.tsx';
import { createViewSet } from './createViewSet.tsx';
import { createView } from './createView.tsx';
import { insertViewSet } from './insertViewSet.tsx';
import { deleteViewSet } from './deleteViewSet.tsx';
import { updateViewSet } from './updateViewSet.tsx';
import { renameViewSet, cancelViewSetRenaming } from './renameViewSet.tsx';
import { deleteView } from './deleteView.tsx';
import { updateView } from './updateView.tsx';
import { renameView, cancelViewRenaming } from './cancelViewRenaming.tsx';
import { addEmoji } from './addEmoji.tsx';
import { deleteEmoji } from './deleteEmoji.tsx';
import { cloneView } from './cloneView.tsx';
import { toggleView } from './toggleView.tsx';
import { rankView } from './rankView.tsx';
import { syncViewName, syncViewEmoji, syncViewDeletion } from './syncViewName.tsx';
import { expandSectionForSelectedView } from './expandSectionForSelectedView.tsx';
import { deleteViewFromState } from './deleteViewFromState.tsx';
import { updateEntityLimits } from './updateEntityLimits.tsx';

const actions = {
	loadViews,
	refreshView,
	refreshViews,
	createViewSet,
	createView,
	insertViewSet,
	deleteViewSet,
	updateViewSet,
	renameViewSet,
	cancelViewSetRenaming,
	deleteView,
	updateView,
	renameView,
	cancelViewRenaming,
	addEmoji,
	deleteEmoji,
	cloneView,
	toggleView,
	rankView,
	syncViewName,
	syncViewEmoji,
	syncViewDeletion,
	expandSectionForSelectedView,
	deleteViewFromState,
	updateEntityLimits,
};

export const NavigationStore: Store<
	State,
	{
		loadViews: ({ tryPreload }?: { tryPreload?: boolean }) => Action<State, Props>;
		refreshView: (viewUUID: string) => Action<State, Props>;
		refreshViews: () => Action<State, Props>;
		createViewSet: (localId: LocalViewId, name: string) => Action<State, Props>;
		createView: (viewType: ViewKind, parentId?: LocalViewId) => Action<State, Props>;
		insertViewSet: () => Action<State, Props>;
		deleteViewSet: (localId: LocalViewId) => Action<State, Props>;
		updateViewSet: (localId: LocalViewId, newName: string) => Action<State, Props>;
		renameViewSet: (localId: LocalViewId) => Action<State, Props>;
		cancelViewSetRenaming: (localId: LocalViewId) => Action<State, Props>;
		deleteView: (localId: LocalViewId) => Action<State, Props>;
		updateView: (localId: LocalViewId, newName: string) => Action<State, Props>;
		renameView: (localId: LocalViewId) => Action<State, Props>;
		cancelViewRenaming: (localId: LocalViewId) => Action<State, Props>;
		addEmoji: (emojiId: string | undefined, localId: LocalViewId) => Action<State, Props>;
		deleteEmoji: (localId: LocalViewId) => Action<State, Props>;
		cloneView: (viewId: LocalViewId, viewType: ViewKind) => Action<State, Props>;
		toggleView: (localId: LocalViewId) => Action<State, Props>;
		rankView: (
			rankPayload: {
				id: string;
				parentId?: string;
				srcParentId?: string;
				index: number;
				isExpandedBeforeDrag: boolean;
			},
			onActionSuccess?: () => void,
			onActionError?: (err?: Error) => void,
		) => Action<State, Props>;
		syncViewName: (viewId: string, type: ViewKind, name: string) => Action<State, Props>;
		syncViewEmoji: (
			viewId: string,
			type: ViewKind,
			emoji: string | undefined,
		) => Action<State, Props>;
		syncViewDeletion: (viewId: string, type: ViewKind) => Action<State, Props>;
		expandSectionForSelectedView: () => Action<State, Props>;
		deleteViewFromState: (viewUUID: string) => Action<State, Props>;
		updateEntityLimits: () => Action<State, Props>;
	},
	Props
> = createStore<State, typeof actions, Props>({
	name: 'NavViewStore',
	initialState: {
		containers: {},
		entityLimits: {},
	},
	actions,
});

export const NavigationContainer: OverrideContainerComponent<Props> = createContainer<
	State,
	typeof actions,
	Props
>(NavigationStore, {
	onInit:
		() =>
		({ dispatch }) => {
			dispatch(actions.expandSectionForSelectedView());
			dispatch(actions.updateEntityLimits());
		},
	onUpdate:
		() =>
		({ dispatch }) => {
			dispatch(actions.expandSectionForSelectedView());
			dispatch(actions.updateEntityLimits());
		},
});

export const useNavigationActions: HookActionsFunction<
	BoundActions<
		State,
		{
			loadViews: ({ tryPreload }?: { tryPreload?: boolean }) => Action<State, Props>;
			refreshView: (viewUUID: string) => Action<State, Props>;
			refreshViews: () => Action<State, Props>;
			createViewSet: (localId: LocalViewId, name: string) => Action<State, Props>;
			createView: (viewType: ViewKind, parentId?: LocalViewId) => Action<State, Props>;
			insertViewSet: () => Action<State, Props>;
			deleteViewSet: (localId: LocalViewId) => Action<State, Props>;
			updateViewSet: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameViewSet: (localId: LocalViewId) => Action<State, Props>;
			cancelViewSetRenaming: (localId: LocalViewId) => Action<State, Props>;
			deleteView: (localId: LocalViewId) => Action<State, Props>;
			updateView: (localId: LocalViewId, newName: string) => Action<State, Props>;
			renameView: (localId: LocalViewId) => Action<State, Props>;
			cancelViewRenaming: (localId: LocalViewId) => Action<State, Props>;
			addEmoji: (emojiId: string | undefined, localId: LocalViewId) => Action<State, Props>;
			deleteEmoji: (localId: LocalViewId) => Action<State, Props>;
			cloneView: (viewId: LocalViewId, viewType: ViewKind) => Action<State, Props>;
			toggleView: (localId: LocalViewId) => Action<State, Props>;
			rankView: (
				rankPayload: {
					id: string;
					parentId?: string;
					srcParentId?: string;
					index: number;
					isExpandedBeforeDrag: boolean;
				},
				onActionSuccess?: () => void,
				onActionError?: (err?: Error) => void,
			) => Action<State, Props>;
			syncViewName: (viewId: string, type: ViewKind, name: string) => Action<State, Props>;
			syncViewEmoji: (
				viewId: string,
				type: ViewKind,
				emoji: string | undefined,
			) => Action<State, Props>;
			syncViewDeletion: (viewId: string, type: ViewKind) => Action<State, Props>;
			expandSectionForSelectedView: () => Action<State, Props>;
			deleteViewFromState: (viewUUID: string) => Action<State, Props>;
			updateEntityLimits: () => Action<State, Props>;
		}
	>
> = createActionsHook(NavigationStore);
